import * as React from "react";
import {Button, Col, Form, Row, Spinner, Table} from "react-bootstrap";
import {CreatableWrapper, SelectWrapper} from "Components/Common/SelectWrapper";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";
import DatePicker from "react-datepicker";
import * as _ from "lodash";
import {Datasource, DatasourceTemplate} from "Stores/superStore/schools";
import {Utils} from "../../../../common/utils";
import {CrudModal} from "Components/Common/CrudModal";
import {
    CellContext,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    Row as RowInfo,
    useReactTable
} from "@tanstack/react-table";
import {ActionWrapper} from "Components/Common/ActionWrapper";
import {useDataSourceTemplates} from "@/hooks/datasource";

interface SettingInputProps {
    settingName: string
    defaultValue?: string | string[]
    help?: string
    onChange: (key, value) => void
    isRequired: boolean
    type: string
}

interface SettingInputState {
    isInvalid: boolean
}

class SettingInputHelper extends React.Component<SettingInputProps, SettingInputState> {
    state = {
        isInvalid: this.props.isRequired ? (this.props.defaultValue === "") : false,
    };

    handleSimpleCheckChange = (value: boolean) => {
        const newValue = value ? "true" : "false";
        this.props.onChange(this.props.settingName, newValue);
    };

    handleChange = (ev: any) => {
        let value = ev.target.value;
        let isInvalid = false;
        if(this.props.isRequired && value === ""){
            isInvalid = true;
        }
        if(this.props.type === "list"){
            const parsed = Utils.commaStringToList(value);
            if(parsed.length === 0) {
                isInvalid = true;
            }
        }
        if(this.props.type === "number" && value !== ""){
            const parsed = parseInt(value);
            if(Number.isNaN(parsed)){
                isInvalid = true;
            } else {
                value = parsed;
            }
        }
        this.setState({isInvalid}, () => {
            this.props.onChange(this.props.settingName, value);
        });
    };

    handleCreatableMultipleSelect = (options: any[]) => {
        let isInvalid = false;
        if(this.props.isRequired && options.length === 0){
            isInvalid = true;
        }
        this.setState({isInvalid}, () => {
            this.props.onChange(this.props.settingName, options);
        });
    };

    render(){
        return (
            <Col>
                {this.props.type === "boolean" ? (
                    <SimpleCheckbox
                        isChecked={this.props.defaultValue === "true"}
                        id={`boolean-${this.props.settingName}`}
                        onChange={this.handleSimpleCheckChange}
                        label={this.props.settingName} />
                ) : this.props.type === "creatable_multiple_select" ? (
                    <>
                        <Form.Label className="mt-2">{this.props.settingName}</Form.Label>
                        <CreatableWrapper
                            isMulti={true}
                            onChange={this.handleCreatableMultipleSelect}
                            placeholder={this.props.settingName}
                            value={this.props.defaultValue}
                            className="mt-2"
                        />
                    </>
                    ) : (
                    <>
                        <Form.Label>{this.props.settingName}</Form.Label>
                        <Form.Control
                            id={`datasource-${this.props.settingName}`}
                            type={this.props.settingName === "password" ? "password" : "text"}
                            defaultValue={this.props.defaultValue}
                            onChange={this.handleChange}
                            isInvalid={this.state.isInvalid}
                        />
                    </>
                )}
                <Form.Text className="text-muted">
                    {this.props.help}
                </Form.Text>
            </Col>
        );
    }
}

interface DatasourcesProps {
    datasources: Datasource[]
    onUpdateDatasource: (datasource: Datasource) => void
    onDeleteDatasource: (datasourceName: string) => void
    isSaving: boolean
    isExistingSchool: boolean
}

interface DatasourcesState {
    showModal: boolean
    isEdit: boolean
    availableTemplates: DatasourceTemplate[]
    selectedTemplate: undefined | DatasourceTemplate
    ingestible: boolean
    refreshable: boolean
    startDate: null | Date
    settings: Record<string, string | string[] | any>
}

export const Datasources = (props: DatasourcesProps) => {
    const DEFAULT_STATE: DatasourcesState = {
        showModal: false,
        isEdit: false,
        availableTemplates: [],
        selectedTemplate: undefined,
        ingestible: false,
        refreshable: false,
        startDate: null,
        settings: {},
    };

    const [state, setState] = React.useState<DatasourcesState>(DEFAULT_STATE);
    const {data: datasourceTemplates} = useDataSourceTemplates();

    const columns: ColumnDef<Datasource>[] = [
        {
            header: "Datasource Name",
            accessorKey: "datasource_name",
            minSize: 300,
            maxSize: 310,
        },
        {
            header: "Ingestible",
            accessorKey: "ingestible",
            cell: (props: CellContext<Datasource, unknown>) => (
                <div>
                    {props.row.original.ingestible ?
                        <i className="icon icon-check topaz-icon" aria-hidden="true"/> : null}
                </div>
            ),
            minSize: 180,
            maxSize: 190,
        },
        {
            header: "Refreshable",
            accessorKey: "refreshable",
            cell: (props) => (
                <div>
                    {props.row.original.refreshable ?
                        <i className="icon icon-check topaz-icon" aria-hidden="true"/> : null}
                </div>
            ),
            minSize: 180,
            maxSize: 190,
        },
        {
            header: "Start Date",
            id: "start_date",
            accessorFn: (d: Datasource) => d.start_date ? Utils.formatDate(new Date(d.start_date), "MM/DD/YYYY") : undefined,
            minSize: 220,
            maxSize: 230,
        },
    ];

    React.useEffect(() => {
        updateAvailableTemplates();
    }, [datasourceTemplates]);

    React.useEffect(() => {
        updateAvailableTemplates();
    }, [props.datasources]);

    const getInitializedTemplateObject = (templateSettings: Record<string, any>) => {
        const ret: Record<string, string> = {};
        Object.keys(templateSettings).forEach((key) => {
            ret[key] = "";
        });
        return ret;
    };

    const updateAvailableTemplates = () => {
        const availableTemplates = _.differenceWith(datasourceTemplates, props.datasources, (arg1, arg2) => arg1.datasource_name === arg2.datasource_name);
        setState(prevState => ({...prevState, availableTemplates}));
    };

    const handleCreateClick = () => {
        if (state.availableTemplates.length > 0) {
            const template: DatasourceTemplate = state.availableTemplates[0];
            setState({
                ...state,
                showModal: true,
                selectedTemplate: template,
                settings: getInitializedTemplateObject(template.settings)
            });
        }
    };

    const handleModalClose = () => {
        setState({
            ...state,
            showModal: false,
            isEdit: false,
            selectedTemplate: undefined,
            ingestible: false,
            refreshable: false,
            startDate: null,
            settings: {},
        });
    };

    const handleEdit = (datasource: Datasource) => {
        const template = datasourceTemplates!.find(template => template.datasource_name === datasource.datasource_name);
        const loadedSettings: Record<string, any> = {};
        Object.keys(template!.settings).forEach(key => {
            const initialValue = datasource.settings[key] || "";
            const settingType = template!.settings[key].type;
            if (settingType === "list") {
                if (initialValue.length > 0) {
                    loadedSettings[key] = Utils.listToCommaString(initialValue);
                }
            } else if (settingType === "creatable_multiple_select") {
                if (initialValue.length > 0) {
                    loadedSettings[key] = Utils.listToOption(initialValue);
                } else {
                    loadedSettings[key] = [];
                }
            } else if (settingType === "number") {
                const parsed = parseInt(initialValue);
                loadedSettings[key] = Number.isNaN(parsed) ? "" : parsed;
            } else {
                loadedSettings[key] = initialValue;
            }
        });
        setState({
            ...state,
            showModal: true,
            isEdit: true,
            selectedTemplate: template,
            ingestible: datasource.ingestible,
            refreshable: datasource.refreshable,
            startDate: datasource.start_date ? new Date(datasource.start_date) : null,
            settings: loadedSettings,
        });
    };

    const handleSave = () => {
        if (state.selectedTemplate === undefined) return;

        const selectedTemplate = state.selectedTemplate!;

        const startDate = new Date(state.startDate!);
        startDate.setUTCHours(6, 0, 0, 0);
        const adjustedStartDate = state.startDate ? startDate : null;

        const nextSettings = state.settings;
        Object.keys(nextSettings).forEach(key => {
            if (selectedTemplate?.settings[key]?.type === "list") {
                nextSettings[key] = Utils.commaStringToList(nextSettings[key] as string);
            } else if (selectedTemplate?.settings[key]?.type === "creatable_multiple_select") {
                nextSettings[key] = Utils.optionToList(nextSettings[key]);
            } else if (selectedTemplate?.settings[key]?.type === "number") {
                nextSettings[key] = parseInt(nextSettings[key] as string);
            }
        });

        delete nextSettings["application_key"];
        delete nextSettings["application_secret"];

        const data: Datasource = {
            datasource_name: selectedTemplate.datasource_name,
            settings: nextSettings,
            refreshable: state.refreshable,
            ingestible: state.ingestible,
            start_date: adjustedStartDate,
        };

        props.onUpdateDatasource(data);
        setState(DEFAULT_STATE);
    };

    const handleDelete = (datasource: Datasource) => {
        props.onDeleteDatasource(datasource.datasource_name);
    };

    const handleTemplateSelect = (selectedTemplate: DatasourceTemplate) => {
        setState({
            ...state,
            isEdit: false,
            selectedTemplate,
            settings: getInitializedTemplateObject(selectedTemplate.settings),
        });
    };

    const handleIngestibleChange = (ingestible: boolean) => {
        setState(prevState => ({...prevState, ingestible}));
    };

    const handleRefreshableChange = (refreshable: boolean) => {
        setState(prevState => ({...prevState, refreshable}));
    };

    const handleStartDateChange = (startDate: Date | null) => {
        setState(prevState => ({...prevState, startDate}));
    };

    const isSettingRequired = (settingName: string) => {
        const settingProperty = (state.selectedTemplate! as DatasourceTemplate).settings[settingName];
        if (props.isExistingSchool && state.isEdit) {
            return settingProperty.required === true && settingProperty.database === true;
        }
        return settingProperty.required === true;
    };

    const isFormIncomplete = () => {
        if (!state.settings || !state.selectedTemplate) {
            return true;
        }
        const templateSettings = (state.selectedTemplate! as DatasourceTemplate).settings;
        const keys = Object.keys(templateSettings);
        for (let i = 0; i < keys.length; i++) {
            const settingName = keys[i];
            if (isSettingRequired(settingName) && state.settings[settingName] === "") {
                return true;
            }
            if (templateSettings[settingName].type === "list") {
                const parsed = Utils.commaStringToList(state.settings[settingName] as string);
                if (parsed.length === 0) {
                    return true;
                }
            }
            if (templateSettings[settingName].type === "number") {
                const parsed = parseInt(state.settings[settingName] as string);
                return isNaN(parsed);
            }
        }
        return false;
    };

    const handleSettingChange = (key: string, value: string) => {
        setState(prevState => ({
            ...prevState,
            settings: {...prevState.settings, [key]: value},
        }));
    };

    const getDatasourceName = (datasource: DatasourceTemplate) => datasource.datasource_name;

    const renderSettings = () => {
        if (state.selectedTemplate === undefined) {
            return null;
        }
        const settings = state.selectedTemplate!.settings;
        delete settings["application_key"]
        delete settings["application_secret"]
        return (
            Object.keys(settings as Record<string, any>).sort().map(key => (
                <Row key={key}>
                    <SettingInputHelper
                        onChange={handleSettingChange}
                        defaultValue={state.settings![key] || ""}
                        help={settings[key].help}
                        isRequired={isSettingRequired(key)}
                        settingName={key}
                        type={settings[key].type}
                    />
                </Row>
            ))
        );
    };

    const renderActionButton = (obj: Datasource) => {
        const actionProps = {
            data: obj,
            onEdit: handleEdit,
            onDelete: handleDelete,
        };
        return (<ActionWrapper {...actionProps} />);
    };

    const renderForm = () => {
        return (
            <fieldset className="fieldset">
                <div className="inner">
                    <Row>
                        <Col>
                            <label>Datasource</label>
                            <SelectWrapper<DatasourceTemplate>
                                name="datasource-name"
                                options={state.availableTemplates}
                                getOptionValue={getDatasourceName}
                                getOptionLabel={getDatasourceName}
                                value={state.selectedTemplate}
                                isDisabled={state.isEdit}
                                onChange={handleTemplateSelect}
                            />
                        </Col>
                    </Row>
                    {renderSettings()}
                    <Row>
                        <Col>
                            <label>Start Date</label>
                            <div>
                                <DatePicker
                                    id="ingestion-start-date"
                                    placeholderText="Select a date..."
                                    selected={state.startDate}
                                    onChange={handleStartDateChange}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <SimpleCheckbox label="Ingestible" isChecked={state.ingestible} id="ingestible"
                                            onChange={handleIngestibleChange}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <SimpleCheckbox label="Refreshable" isChecked={state.refreshable} id="refreshable"
                                            onChange={handleRefreshableChange}/>
                        </Col>
                    </Row>
                </div>
            </fieldset>
        );
    };

    const customColumns: ColumnDef<Datasource>[] = [...columns, {
        header: "Actions",
        cell: ({row}: { row: RowInfo<Datasource> }) => renderActionButton(row.original),
        size: 80,
    }];

    return (
        <React.Fragment>
            <Row className="button-bar form-buttons">
                <Col className="text-right">
                    <div className="actions">
                        <Button
                            onClick={handleCreateClick}
                            disabled={state.availableTemplates.length === 0}
                            className="add-datasource-btn">
                            add datasource
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row className={"list"}>
                {props.isSaving
                    ? <Col md={1} lg={1}><Spinner animation="border"/></Col>
                    : <DatasourcesReactTable data={props.datasources} columns={customColumns}/>}
            </Row>
            {state.showModal
                ? <CrudModal
                    typeLabel={"Datasource"}
                    isEdit={state.isEdit}
                    renderForm={renderForm}
                    onClose={handleModalClose}
                    onSave={handleSave}
                    isDisabled={isFormIncomplete()}
                    showSpinner={props.isSaving}/>
                : null}
        </React.Fragment>
    );
};

interface DatasourcesReactTableProps {
    data: Datasource[]
    columns: ColumnDef<Datasource>[]
}

function DatasourcesReactTable({data, columns}: DatasourcesReactTableProps) {
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <Table>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                        <th key={header.id}>
                            {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                        </th>
                    ))}
                </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (<tr><td colSpan={5} className="text-center">No Datasources</td></tr>)}
            </tbody>
        </Table>
    );
}
